import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPrint,
  faQuestion,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import { faCommentDots } from '@fortawesome/free-regular-svg-icons';

import { ToolbarButton } from '../ToolbarButton';
import { FontSizePopover } from './FontSizePopover';
import { DownloadMenuPopover } from './DownloadMenuPopover';
import { FeedbackPopover } from './FeedbackPopover';
import type { ProjectFeedbackProps } from '../../hooks/survey';
import type { FingeringSize } from '../FingeringOverlay';
import toolbarFontSizeIcon from '@Assets/images/toolbar-fontsize.png';

export type XmlDownloadAvailability = 'ready' | 'preparing' | 'unavailable';

export interface ProjectActionButtonsProps {
  showFontSize?: boolean;
  fontPopoverOpen: boolean;
  onFontPopoverOpenChange: (open: boolean) => void;
  fontSize: FingeringSize;
  onFontSizeChange: (next: FingeringSize) => void;
  /** Pitch-letter slider. Hidden when the callback is omitted. */
  pitchSize?: FingeringSize;
  onPitchSizeChange?: (next: FingeringSize) => void;

  onPrint?: () => void;
  onShowTutorial?: () => void;

  downloadPopoverOpen: boolean;
  onDownloadPopoverOpenChange: (open: boolean) => void;
  onDownloadPdf?: () => void;
  isDownloadingPdf: boolean;
  onDownloadXml?: () => void;
  isDownloadingXml: boolean;
  onDownloadXmlSibelius?: () => void;
  isDownloadingXmlSibelius?: boolean;
  onDownloadXmlPlain?: () => void;
  isDownloadingXmlPlain?: boolean;
  /** Shown after an engraved render has produced a MIDI file. */
  onDownloadMidi?: () => void;
  /** Always show the MusicXML button. `ready` enables it; the other
   *  two disable it with an explanatory title instead of hiding it. */
  xmlAvailability?: XmlDownloadAvailability;

  feedback?: ProjectFeedbackProps;

  popoverPlacement?: 'bottom' | 'top';
  className?: string;
}

const DownloadFormatGlyph: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex flex-col items-center leading-none">
    <FontAwesomeIcon icon={faArrowDown} className="text-[11px] md:text-xs" />
    <span className="mt-0.5 text-[10px] font-bold tracking-wide md:text-[11px]">
      {label}
    </span>
  </span>
);

/**
 * Font Size / Print / PDF / MusicXML / MIDI / Feedback / Help group
 * shared by the top toolbar and the mobile bottom bar.
 */
export const ProjectActionButtons: React.FC<ProjectActionButtonsProps> = ({
  showFontSize = true,
  fontPopoverOpen,
  onFontPopoverOpenChange,
  fontSize,
  onFontSizeChange,
  pitchSize,
  onPitchSizeChange,
  onPrint,
  onShowTutorial,
  downloadPopoverOpen,
  onDownloadPopoverOpenChange,
  onDownloadPdf,
  isDownloadingPdf,
  onDownloadXml,
  isDownloadingXml,
  onDownloadXmlSibelius,
  isDownloadingXmlSibelius = false,
  onDownloadXmlPlain,
  isDownloadingXmlPlain = false,
  onDownloadMidi,
  xmlAvailability,
  feedback,
  popoverPlacement = 'bottom',
  className = '',
}) => {
  const fontBtnRef = useRef<HTMLButtonElement | null>(null);
  const xmlBtnRef = useRef<HTMLButtonElement | null>(null);
  const feedbackBtnRef = useRef<HTMLButtonElement | null>(null);

  const xmlState: XmlDownloadAvailability =
    xmlAvailability ?? (onDownloadXml ? 'ready' : 'unavailable');
  const showMusicXml = xmlAvailability !== undefined || Boolean(onDownloadXml);
  const xmlReady =
    xmlState === 'ready' &&
    Boolean(onDownloadXml || onDownloadXmlSibelius || onDownloadXmlPlain);
  const hasFeedback = Boolean(feedback);
  const xmlBusy =
    isDownloadingXml || isDownloadingXmlSibelius || isDownloadingXmlPlain;

  const xmlTitle =
    xmlState === 'preparing'
      ? 'Preparing MusicXML…'
      : xmlState === 'unavailable'
        ? 'MusicXML not available for this scan'
        : xmlBusy
          ? 'Download in progress…'
          : 'Download MusicXML';

  useEffect(() => {
    if (!feedback?.open) return;
    onFontPopoverOpenChange(false);
    onDownloadPopoverOpenChange(false);
  }, [feedback?.open, onFontPopoverOpenChange, onDownloadPopoverOpenChange]);

  const closeSiblingPopovers = (keep: 'font' | 'download' | 'feedback') => {
    if (keep !== 'font') onFontPopoverOpenChange(false);
    if (keep !== 'download') onDownloadPopoverOpenChange(false);
    if (keep !== 'feedback' && feedback?.open) feedback.onOpenChange(false);
  };

  return (
    <div
      className={`relative flex shrink-0 items-center gap-x-0 md:gap-x-1 ${className}
      `}
    >
      {showFontSize && (
        <ToolbarButton
          ref={fontBtnRef}
          variant="ghost"
          layout="icon"
          title={
            onPitchSizeChange
              ? 'Adjust fingering and pitch letter size'
              : 'Adjust fingering number size'
          }
          aria-haspopup="dialog"
          aria-expanded={fontPopoverOpen}
          onClick={() => {
            const next = !fontPopoverOpen;
            onFontPopoverOpenChange(next);
            if (next) closeSiblingPopovers('font');
          }}
          className="h-11 min-h-11 w-11 min-w-11 md:h-14 md:w-14"
          data-tour="fontsize"
        >
          <img
            src={toolbarFontSizeIcon}
            alt=""
            className="h-7 w-7 [filter:brightness(0)] md:h-10 md:w-10"
            draggable={false}
          />
        </ToolbarButton>
      )}

      {onPrint && (
        <ToolbarButton
          variant="ghost"
          layout="icon"
          title="Print the sheet music"
          onClick={onPrint}
          className="h-11 min-h-11 w-11 min-w-11 md:h-14 md:w-14"
        >
          <FontAwesomeIcon icon={faPrint} className="text-xl text-gray-800" />
        </ToolbarButton>
      )}

      {(onDownloadPdf || showMusicXml) && (
        <div
          className="flex items-center gap-x-0 md:gap-x-1"
          data-tour="download"
        >
          {onDownloadPdf && (
            <ToolbarButton
              variant="ghost"
              layout="icon"
              title={isDownloadingPdf ? 'Generating PDF…' : 'Download PDF'}
              aria-label="Download PDF"
              onClick={onDownloadPdf}
              disabled={isDownloadingPdf}
              className="h-11 min-h-11 w-11 min-w-11 md:h-14 md:w-14"
              data-tour="download-pdf"
            >
              <DownloadFormatGlyph label="PDF" />
            </ToolbarButton>
          )}

          {showMusicXml && (
            <div
              className="relative flex items-center"
              data-tour="download-xml"
            >
              <ToolbarButton
                ref={xmlBtnRef}
                variant="ghost"
                layout="icon"
                title={xmlTitle}
                aria-label="Download MusicXML"
                aria-haspopup="menu"
                aria-expanded={downloadPopoverOpen}
                onClick={() => {
                  if (!xmlReady) return;
                  const next = !downloadPopoverOpen;
                  onDownloadPopoverOpenChange(next);
                  if (next) closeSiblingPopovers('download');
                }}
                disabled={!xmlReady || xmlBusy}
                className="h-11 min-h-11 w-11 min-w-11 md:h-14 md:w-14"
              >
                <DownloadFormatGlyph label="XML" />
              </ToolbarButton>
              {xmlReady && downloadPopoverOpen && (
                <DownloadMenuPopover
                  onDownloadXmlPlain={onDownloadXmlPlain}
                  onDownloadXml={onDownloadXml}
                  onDownloadXmlSibelius={onDownloadXmlSibelius}
                  isDownloadingXmlPlain={isDownloadingXmlPlain}
                  isDownloadingXml={isDownloadingXml}
                  isDownloadingXmlSibelius={isDownloadingXmlSibelius}
                  onClose={() => onDownloadPopoverOpenChange(false)}
                  anchorRef={xmlBtnRef}
                  placement={popoverPlacement}
                />
              )}
            </div>
          )}
        </div>
      )}

      {onDownloadMidi && (
        <ToolbarButton
          variant="ghost"
          layout="icon"
          title="Download MIDI"
          aria-label="Download MIDI"
          data-tour="midi"
          onClick={() => {
            onFontPopoverOpenChange(false);
            onDownloadPopoverOpenChange(false);
            if (feedback?.open) feedback.onOpenChange(false);
            onDownloadMidi();
          }}
          className="h-11 min-h-11 w-11 min-w-11 md:h-14 md:w-14"
        >
          <DownloadFormatGlyph label="MIDI" />
        </ToolbarButton>
      )}

      {hasFeedback && feedback && (
        <ToolbarButton
          ref={feedbackBtnRef}
          variant="ghost"
          layout="icon"
          title="Give feedback"
          aria-label="Give feedback"
          aria-haspopup="dialog"
          aria-expanded={feedback.open}
          onClick={() => {
            const next = !feedback.open;
            if (next) {
              feedback.onManualOpen?.();
              closeSiblingPopovers('feedback');
            }
            feedback.onOpenChange(next);
          }}
          className="h-11 min-h-11 w-11 min-w-11 md:h-14 md:w-14"
        >
          <FontAwesomeIcon
            icon={faCommentDots}
            className="text-xl text-gray-800"
          />
        </ToolbarButton>
      )}

      {onShowTutorial && (
        <ToolbarButton
          variant="ghost"
          layout="icon"
          title="Show project tutorial"
          aria-label="Show project tutorial"
          onClick={onShowTutorial}
          className="h-11 min-h-11 w-11 min-w-11 md:h-14 md:w-14"
          data-tour="help"
        >
          <FontAwesomeIcon
            icon={faQuestion}
            className="text-xl text-gray-800"
          />
        </ToolbarButton>
      )}

      {showFontSize && fontPopoverOpen && (
        <FontSizePopover
          value={fontSize}
          onChange={onFontSizeChange}
          pitchSize={pitchSize}
          onPitchSizeChange={onPitchSizeChange}
          onClose={() => onFontPopoverOpenChange(false)}
          anchorRef={fontBtnRef}
          placement={popoverPlacement}
        />
      )}
      {hasFeedback && feedback?.open && (
        <FeedbackPopover
          projectId={feedback.projectId}
          pageType={feedback.pageType}
          trigger={feedback.trigger}
          onClose={() => feedback.onOpenChange(false)}
          onSubmitted={feedback.onSubmitted}
          anchorRef={feedbackBtnRef}
          placement={popoverPlacement}
        />
      )}
    </div>
  );
};
